import Checkbox from '@ben/orion/checkbox';
import { Modal } from '@ben/orion/modals';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Metadata, Muted } from './shared';
import type { RunChoice } from './use-start-run';

/**
 * Confirms a run before it starts, and owns the two answers it collects.
 *
 * Which programme, and — for a build — whether to apply every proposal
 * without review. A build always confirms even with a single candidate,
 * because that is where the auto-approve choice is made; an update with one
 * candidate never reaches here.
 */
export const StartRunModal = ({
  choice,
  onConfirm,
  onClose,
  isStarting = false,
}: {
  choice: RunChoice | null;
  onConfirm: (programmeId: number, autoApprove: boolean) => void;
  onClose: () => void;
  isStarting?: boolean;
}) => {
  const [selectedProgramme, setSelectedProgramme] = useState<number | null>(
    null
  );
  const [autoApprove, setAutoApprove] = useState(true);

  // Each opening is its own question. Held across them, a programme picked for
  // one run stayed selected for the next, which could enable the confirm
  // button for a programme that is not even in the new list.
  useEffect(() => {
    setSelectedProgramme(null);
    setAutoApprove(true);
  }, [choice]);

  const isBuild = choice?.operation === 'CREATE';
  const onlyProgramme =
    choice?.programmes.length === 1 ? choice.programmes[0] : undefined;
  // A lone candidate needs no picking, but the dialog still opens for the
  // auto-approve choice — so the confirm guard reads the resolved programme
  // rather than only what was clicked.
  const chosenProgramme = selectedProgramme ?? onlyProgramme?.id ?? null;

  return (
    <Modal
      isOpen={choice !== null}
      title={isBuild ? 'Start a build run' : 'Which programme?'}
      description={
        isBuild
          ? 'A build run creates the benefit from these requirements.'
          : 'This employer has more than one programme.'
      }
      primaryText="Start run"
      secondaryText="Cancel"
      primaryProps={{
        disabled: chosenProgramme === null || isStarting,
        isLoading: isStarting,
      }}
      onConfirm={() => {
        if (chosenProgramme !== null) onConfirm(chosenProgramme, autoApprove);
      }}
      onClose={onClose}
    >
      <ModalBody>
        {onlyProgramme ? (
          <Metadata>
            <dt>Programme</dt>
            <dd>{onlyProgramme.name}</dd>
          </Metadata>
        ) : (
          <ProgrammeChoices>
            {/* A fieldset and legend, because the radios alone do not say
                what is being chosen — to a screen reader or to anyone else. */}
            <legend>
              <Typography variant={TypographyVariant.bodyMd2}>
                Which programme does this run belong to?
              </Typography>
            </legend>
            {choice?.programmes.map((programme) => (
              <label key={programme.id} htmlFor={`programme-${programme.id}`}>
                <input
                  id={`programme-${programme.id}`}
                  type="radio"
                  name="programme"
                  value={programme.id}
                  checked={chosenProgramme === programme.id}
                  onChange={() => setSelectedProgramme(programme.id)}
                />
                {programme.name}
              </label>
            ))}
          </ProgrammeChoices>
        )}

        {isBuild ? (
          <AutoApprove>
            <Checkbox
              label="Apply automatically"
              value={autoApprove}
              onChange={setAutoApprove}
            />
            <Muted>
              Creates the benefit and applies every proposal without review. It
              stays unpublished until someone verifies and activates it. Untick
              to approve the new benefit yourself first.
            </Muted>
          </AutoApprove>
        ) : null}
      </ModalBody>
    </Modal>
  );
};

const ModalBody = styled.div`
  ${({ theme }) => css`
    display: grid;
    gap: ${theme.spacing(3)};
    text-align: left;
  `}
`;

const AutoApprove = styled.div`
  ${({ theme }) => css`
    display: grid;
    gap: ${theme.spacing(1)};
    border-top: 1px solid ${theme.palette.neutral[40]};
    padding-top: ${theme.spacing(3)};
    text-align: left;
  `}
`;

const ProgrammeChoices = styled.fieldset`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    text-align: left;
    max-height: 320px;
    overflow-y: auto;
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;

    /* The question needs to sit apart from the dialog's own description. */
    > legend {
      margin-bottom: ${theme.spacing(1)};
    }

    label {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
      font-size: 0.875rem;
    }
  `}
`;
